import { ChevronDown, ChevronUp, Maximize2, Minus, Plus, ZoomIn, ZoomOut } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

import type { Finding, FindingId } from "../data/review-data"

const ZOOM_STOPS: number[] = [0.5, 0.75, 1, 1.25, 1.5, 2]

export interface PdfControlsProps {
  findings: Finding[]
  activeFindingId: FindingId | null
  onSelectFinding: (id: FindingId) => void
  page: number
  pageCount: number
  onPageChange: (page: number) => void
  zoom: number
  onZoomChange: (zoom: number) => void
  /** Frame the active finding: bump zoom + scroll its bbox into view. */
  onZoomToActive?: () => void
  /** True while framed on the active finding — the button offers to zoom out. */
  isFramed?: boolean
  onToggleFullscreen?: () => void
  className?: string
}

export function PdfControls({
  findings,
  activeFindingId,
  onSelectFinding,
  page,
  pageCount,
  onPageChange,
  zoom,
  onZoomChange,
  onZoomToActive,
  isFramed = false,
  onToggleFullscreen,
  className,
}: PdfControlsProps): JSX.Element {
  const activeIndex = findings.findIndex((f) => f.id === activeFindingId)
  const active = activeIndex >= 0 ? findings[activeIndex] : null

  const step = (delta: -1 | 1) => {
    if (findings.length === 0) return
    const next =
      activeIndex < 0
        ? delta === 1
          ? 0
          : findings.length - 1
        : (activeIndex + delta + findings.length) % findings.length
    const target = findings[next]
    if (target) onSelectFinding(target.id)
  }

  const zoomStep = (delta: -1 | 1) => {
    // Step relative to the nearest stop (not the first ≥ current) so a
    // non-canonical zoom lands on the right side of the ramp.
    const nearest = ZOOM_STOPS.reduce(
      (best, v, i) =>
        Math.abs(v - zoom) < Math.abs((ZOOM_STOPS[best] ?? v) - zoom) ? i : best,
      0,
    )
    const nextIdx = Math.max(0, Math.min(ZOOM_STOPS.length - 1, nearest + delta))
    const nextValue = ZOOM_STOPS[nextIdx]
    if (nextValue != null && nextValue !== zoom) onZoomChange(nextValue)
  }

  return (
    <div
      className={cn(
        "pointer-events-auto inline-flex h-[34px] items-center gap-1 rounded-full border border-border bg-surface/95 px-1.5 text-text-2 shadow-e3 backdrop-blur",
        className,
      )}
    >
      <PillButton label="Previous finding" onClick={() => step(-1)} disabled={findings.length === 0}>
        <ChevronUp className="size-3.5" aria-hidden="true" />
      </PillButton>

      <div className="flex min-w-[136px] items-center justify-center gap-1.5 px-2 text-[11.5px] tabular-nums text-text-2">
        {active ? (
          <>
            {/* Show the finding's stable number (what appears on the box
                badge), not its index in the rail order — otherwise the pill
                reads "3" while the badge in the gutter reads "1". */}
            <FindingHoverChip finding={active} />
            {onZoomToActive && active.bbox && (
              <button
                type="button"
                onClick={onZoomToActive}
                aria-label={isFramed ? "Reset zoom" : "Zoom in on this finding"}
                title={isFramed ? "Reset zoom" : "Zoom in on this finding"}
                className={cn(
                  "ml-0.5 grid size-[20px] shrink-0 place-items-center rounded-full transition-colors duration-120 ease-out",
                  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
                  isFramed
                    ? "bg-[color:var(--brand)] text-white hover:bg-[color:var(--brand)]/90"
                    : "bg-[color:var(--brand)]/12 text-[color:var(--brand)] hover:bg-[color:var(--brand)]/22",
                )}
              >
                {isFramed ? (
                  <ZoomOut className="size-3" aria-hidden="true" />
                ) : (
                  <ZoomIn className="size-3" aria-hidden="true" />
                )}
              </button>
            )}
          </>
        ) : (
          <span className="text-text-3">
            {findings.length} finding{findings.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      <PillButton label="Next finding" onClick={() => step(1)} disabled={findings.length === 0}>
        <ChevronDown className="size-3.5" aria-hidden="true" />
      </PillButton>

      {pageCount > 1 && (
        <>
          <PillDivider />
          <PillButton
            label="Previous page"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
          >
            <ChevronUp className="size-3.5" aria-hidden="true" />
          </PillButton>
          <span className="px-1 font-id text-[10.5px] tabular-nums text-text-2">
            {page} / {pageCount}
          </span>
          <PillButton
            label="Next page"
            onClick={() => onPageChange(Math.min(pageCount, page + 1))}
            disabled={page >= pageCount}
          >
            <ChevronDown className="size-3.5" aria-hidden="true" />
          </PillButton>
        </>
      )}

      <PillDivider />

      <PillButton label="Zoom out" onClick={() => zoomStep(-1)}>
        <Minus className="size-3.5" aria-hidden="true" />
      </PillButton>
      <button
        type="button"
        onClick={() => onZoomChange(1)}
        className="rounded-full px-1.5 font-id text-[10.5px] tabular-nums text-text-2 transition-colors duration-120 ease-out hover:bg-surface-sunken hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]"
        aria-label="Reset zoom to 100%"
      >
        {Math.round(zoom * 100)}%
      </button>
      <PillButton label="Zoom in" onClick={() => zoomStep(1)}>
        <Plus className="size-3.5" aria-hidden="true" />
      </PillButton>
      {onToggleFullscreen && (
        <>
          <PillDivider />
          <PillButton label="Fullscreen" onClick={onToggleFullscreen}>
            <Maximize2 className="size-3.5" aria-hidden="true" />
          </PillButton>
        </>
      )}
    </div>
  )
}

/**
 * A ghost button sized for the floating pill. Not the same as IconButton
 * because the pill needs a lighter footprint (26px square, transparent
 * unless hovered).
 */
function PillButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        "grid size-[26px] shrink-0 place-items-center rounded-full text-text-2 transition-colors duration-120 ease-out",
        "hover:bg-surface-sunken hover:text-text",
        "disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
      )}
    >
      {children}
    </button>
  )
}

function PillDivider(): JSX.Element {
  return <span aria-hidden="true" className="h-4 w-px bg-border" />
}

/**
 * Hoverable summary of the active finding, so a reviewer can see rule,
 * claim, divergence and confidence without opening the rail. Hover- AND
 * focus-triggered so keyboard users get the same affordance.
 */
/**
 * Pure-CSS hover chip for the pill's active finding. Renders a summary card
 * on hover/focus without any state, event handlers, or portalling — so it
 * cannot capture clicks on sibling controls (the zoom button) or linger
 * after the reviewer has moved on.
 *
 * A previous version used Radix Popover with mouse enter/leave timers; on
 * hover it stayed open, covering the finding once the zoom animation
 * scrolled it into place. Reviewers had to hunt for their mouse to dismiss
 * it. This version disappears the moment the pointer leaves the chip.
 */
function FindingHoverChip({ finding }: { finding: Finding }): JSX.Element {
  const divergenceText =
    finding.divergence && typeof finding.divergence === "object"
      ? (finding.divergence as { master?: string | null; printed?: string | null })
      : null

  return (
    <span className="group/chip relative flex items-center gap-1.5">
      <span className="font-medium text-text">Finding {finding.number}</span>
      <span className="text-text-3">·</span>
      <span className="font-id text-[10.5px] uppercase tracking-[0.06em] text-text-3">
        {finding.ruleId}
      </span>
      <span
        role="tooltip"
        className={cn(
          "pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-80 -translate-x-1/2",
          "rounded-md border border-border bg-surface p-3 text-left text-body-sm shadow-e3",
          "opacity-0 transition-opacity duration-100 ease-out",
          "group-hover/chip:opacity-100 group-focus-within/chip:opacity-100",
        )}
      >
        <span className="mb-1 flex items-center gap-1.5">
          <span className="font-id text-caption uppercase tracking-[0.06em] text-text-3">
            {finding.ruleId}
          </span>
          <span className="text-text-3">·</span>
          <span
            className={cn(
              "text-caption font-medium",
              finding.severity === "error"
                ? "text-[color:var(--err)]"
                : finding.severity === "warning"
                  ? "text-[color:var(--print)]"
                  : "text-[color:var(--info)]",
            )}
          >
            {finding.severity}
          </span>
        </span>
        <span className="mb-2 block text-text">{finding.claim}</span>
        {divergenceText && (divergenceText.master || divergenceText.printed) && (
          <span className="mb-2 block space-y-0.5 text-caption">
            {divergenceText.master != null && (
              <span className="block">
                <span className="font-medium text-text-2">Master: </span>
                <span className="text-text-2">{divergenceText.master || "—"}</span>
              </span>
            )}
            {divergenceText.printed != null && (
              <span className="block">
                <span className="font-medium text-text-2">Printed: </span>
                <span className="text-text-2">{divergenceText.printed || "—"}</span>
              </span>
            )}
          </span>
        )}
        <span className="block text-caption text-text-3">
          {finding.axes.origin === "manual"
            ? "Manual finding"
            : finding.confidence != null
              ? `${Math.round(finding.confidence * 100)}% confidence`
              : "—"}
        </span>
      </span>
    </span>
  )
}
